'use client';

import React, { useRef } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NewsArticle } from '../../types/news';
import { categoryAccent } from './newsCategoryStyles';

interface NewsSpotlightCarouselProps {
  articles: NewsArticle[];
}

function formatDate(isoDate: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${isoDate}T12:00:00Z`));
}

const NewsSpotlightCarousel: React.FC<NewsSpotlightCarouselProps> = ({ articles }) => {
  const trackRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    const track = trackRef.current;
    if (!track) return;
    const amount = track.clientWidth * 0.88;
    track.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  if (articles.length === 0) return null;

  return (
    <div className="relative">
      <div className="mb-3 flex items-end justify-between gap-3 sm:mb-4">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pink-300/80 sm:text-[11px] sm:tracking-[0.24em]">
            Editor&apos;s picks
          </p>
          <h2 className="text-lg font-bold text-white sm:text-xl md:text-2xl">Top stories this week</h2>
          <p className="mt-1 hidden text-sm text-pink-200/65 sm:block">
            The biggest launches and platform moves from our AI girlfriend news desk.
          </p>
        </div>
        <div className="hidden shrink-0 gap-2 sm:flex">
          <button
            type="button"
            onClick={() => scroll('left')}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-pink-100 transition hover:bg-white/10"
            aria-label="Scroll spotlight left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-pink-100 transition hover:bg-white/10"
            aria-label="Scroll spotlight right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <p className="mb-2 text-xs text-pink-200/50 sm:hidden">Swipe to browse picks</p>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[#07050f] to-transparent sm:hidden" />

        <div
          ref={trackRef}
          className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain px-4 pb-1 touch-pan-x sm:gap-4 md:mx-0 md:px-0"
        >
          {articles.map((article) => {
            const accent = categoryAccent[article.category];
            return (
              <Link
                key={article.slug}
                href={`/news/${article.slug}`}
                className="group relative w-[min(82vw,320px)] shrink-0 snap-center overflow-hidden rounded-2xl border border-white/10 sm:w-[70vw] sm:rounded-3xl md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.67rem)]"
              >
                <div className="relative aspect-[4/5] max-h-[420px] sm:aspect-[3/4] sm:max-h-none">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    loading="lazy"
                    width={480}
                    height={600}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950 via-charcoal-950/50 to-charcoal-950/10" />
                  <div className={`absolute left-3 top-3 h-1 w-8 rounded-full sm:left-4 sm:top-4 sm:w-10 ${accent.dot}`} />

                  <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 md:p-6">
                    <div className="mb-2 flex flex-wrap items-center gap-1.5 sm:mb-3 sm:gap-2">
                      <span
                        className={`rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:px-2 sm:text-[10px] ${accent.border} ${accent.bg} ${accent.text}`}
                      >
                        {article.category}
                      </span>
                      <span className="text-[11px] text-pink-200/60 sm:text-xs">
                        {formatDate(article.publishDate)}
                      </span>
                    </div>
                    <h3 className="line-clamp-3 text-lg font-bold leading-tight text-white transition group-hover:text-pink-100 sm:text-xl md:text-2xl">
                      {article.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 text-xs text-pink-100/75 sm:mt-2 sm:text-sm">
                      {article.excerpt}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NewsSpotlightCarousel;
